import { createFileRoute, type ErrorComponentProps, Outlet, useRouter } from '@tanstack/react-router';
import { format } from 'date-fns';
import { ArrowLeftRight, Building2, Calendar1, ChartColumnIncreasing, FileText, Settings, Users2 } from 'lucide-react';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { OrganizationLogo } from '@/components/OrganizationLogo';
import { Page } from '@/components/Page';
import { RouteError } from '@/components/RouteError';
import { Section } from '@/components/Section';
import { Skeleton } from '@/components/Skeleton';
import { type RouteTab, RouteTabs } from '@/components/Tabs';
import { fetchOrganization } from '@/queries/organizations';
import { EditOrganizationDialog } from './-fragments/EditOrganizationDialog';
import { useUpdateOrganization } from './-hooks';
import * as styles from './route.css';

const ORGANIZATION_TABS: RouteTab[] = [
    { to: '/system/organizations/$organizationId/profile', label: 'プロフィール', icon: Building2 },
    { to: '/system/organizations/$organizationId/surveys', label: 'サーベイ一覧', icon: ChartColumnIncreasing },
    { to: '/system/organizations/$organizationId/reports', label: 'レポート一覧', icon: FileText },
    { to: '/system/organizations/$organizationId/mapping', label: 'サーベイマッピング', icon: ArrowLeftRight },
    { to: '/system/organizations/$organizationId/survey-settings', label: 'サーベイ設定', icon: Settings },
    { to: '/system/organizations/$organizationId/users', label: 'ユーザー管理', icon: Users2 },
    { to: '/system/organizations/$organizationId/others', label: 'その他設定', icon: Settings },
];

export const Route = createFileRoute('/system/_auth/organizations/$organizationId')({
    component: SystemShowOrganizationRoute,
    loader: async ({ params }) => {
        const { organizationId } = params;
        return fetchOrganization({ uuid: organizationId });
    },
    pendingComponent: SystemShowOrganizationRouteSkeleton,
    errorComponent: SystemShowOrganizationRouteError,
});

function SystemShowOrganizationRouteError({ reset }: ErrorComponentProps) {
    return <RouteError kind="system" onRetry={reset} />;
}

function SystemShowOrganizationRoute() {
    const org = Route.useLoaderData();
    const router = useRouter();

    const updateInfoMutation = useUpdateOrganization(org.uuid, 'info', () => router.invalidate());

    return (
        <Page.Root>
            <Page.BackLink to="/system/organizations" label="クライアント一覧に戻る" className={styles.backLink} />

            <Page.Content>
                <Section pb="6">
                    <Flex as="header" align="center" gap="5">
                        <OrganizationLogo
                            imgSrc={org.website ? `https://www.google.com/s2/favicons?domain=${org.website}` : ''}
                            imgAlt={org.name}
                            size="l"
                        />
                        <Flex direction="column" gap="2">
                            <Flex align="center" gap="2" className={styles.organizationName}>
                                {org.name}
                                <EditOrganizationDialog
                                    org={org}
                                    isSubmitting={updateInfoMutation.isPending}
                                    onSubmit={updateInfoMutation.mutateAsync}
                                />
                            </Flex>
                            <Flex className={styles.headerMeta} gap="4">
                                <Flex align="center" gap="1" title="業界">
                                    <Icon icon={Building2} size="xs" />
                                    {org.industry.displayName}
                                </Flex>
                                <Flex align="center" gap="1" title="従業員数">
                                    <Icon icon={Users2} size="xs" />
                                    {org.companySize.displayName}
                                </Flex>
                                <Flex align="center" gap="1" title="登録年月日">
                                    <Icon icon={Calendar1} size="xs" />
                                    {format(org.createdAt, 'yyyy/MM/dd')}
                                </Flex>
                            </Flex>
                        </Flex>
                    </Flex>
                </Section>

                <RouteTabs tabs={ORGANIZATION_TABS} />

                <Outlet />
            </Page.Content>
        </Page.Root>
    );
}

function SystemShowOrganizationRouteSkeleton() {
    return (
        <Page.Root>
            <Page.BackLink to="/system/organizations" label="クライアント一覧に戻る" className={styles.backLink} />

            <Page.Content>
                <Section pb="6">
                    <Flex as="header" align="center" gap="5">
                        <Skeleton.Circle radius={80} />
                        <Flex direction="column" gap="2">
                            <Skeleton.Line width={220} height="xl" />
                            <Flex className={styles.headerMeta} gap="4">
                                <Skeleton.Line width={90} height="sm" />
                                <Skeleton.Line width={90} height="sm" />
                                <Skeleton.Line width={130} height="sm" />
                            </Flex>
                            <Skeleton.Box width={90} height={24} />
                        </Flex>
                    </Flex>
                </Section>

                <RouteTabs tabs={ORGANIZATION_TABS} />
            </Page.Content>
        </Page.Root>
    );
}
